import { Build } from '@stencil/core';

/**
 * Strict, JS-gated lazy loading for author-provided `<img>` elements that live
 * inside a component's light-DOM slot content (raw HTML the backend projects
 * into `<slot>`).
 *
 * Such images can only carry native `loading="lazy"`, and Chrome's native lazy
 * loading uses a generous viewport-distance threshold — so below-the-fold
 * images still get fetched on initial page load. `<dh-picture>` avoids this by
 * withholding the real `src` on the client and restoring it via an
 * `IntersectionObserver`; this helper applies the same technique to slotted
 * images the component itself does not author.
 *
 * The real `src`/`srcset` is stashed into `data-lazy-src`/`data-lazy-srcset`
 * and removed from the element, cancelling any pending native fetch, then
 * restored once the image scrolls within `rootMargin` of the viewport.
 *
 * SSR-safe: the whole body is gated behind `Build.isBrowser` (statically
 * `false` in the hydrate build), so the server keeps the real `src` in the HTML
 * for SEO and no-JS visitors.
 *
 * @param root - Element whose descendant `<img>` elements should be lazified.
 * @param rootMargin - Distance from the viewport at which to start loading.
 * @returns Cleanup function that disconnects the observer.
 *
 * @example
 * ```ts
 * componentDidLoad() {
 *   this.cleanup = lazyLoadImages(this.host);
 * }
 * disconnectedCallback() {
 *   this.cleanup?.();
 * }
 * ```
 */
export function lazyLoadImages(root: HTMLElement | undefined, rootMargin = '200px'): () => void {
  const noop = () => undefined;
  if (!Build.isBrowser || !root) return noop;

  const images = Array.from(root.querySelectorAll('img'));
  const restore = (img: HTMLImageElement) => {
    const src = img.getAttribute('data-lazy-src');
    const srcSet = img.getAttribute('data-lazy-srcset');
    if (src !== null) {
      img.setAttribute('src', src);
      img.removeAttribute('data-lazy-src');
    }
    if (srcSet !== null) {
      img.setAttribute('srcset', srcSet);
      img.removeAttribute('data-lazy-srcset');
    }
  };

  const pending: HTMLImageElement[] = [];
  for (const img of images) {
    // Already downloaded (cached/above the fold) — leave it alone.
    if (img.complete && img.naturalWidth > 0) continue;

    const src = img.getAttribute('src');
    if (src) {
      img.setAttribute('data-lazy-src', src);
      img.removeAttribute('src');
    }
    const srcSet = img.getAttribute('srcset');
    if (srcSet) {
      img.setAttribute('data-lazy-srcset', srcSet);
      img.removeAttribute('srcset');
    }
    if (src || srcSet) pending.push(img);
  }

  if (pending.length === 0) return noop;

  // Without IntersectionObserver support, restore everything immediately so no
  // image stays permanently blank.
  if (typeof IntersectionObserver === 'undefined') {
    pending.forEach(restore);
    return noop;
  }

  const observer = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        restore(entry.target as HTMLImageElement);
        observer.unobserve(entry.target);
      }
    },
    { rootMargin },
  );

  pending.forEach(img => observer.observe(img));

  return () => observer.disconnect();
}
